import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer, FinalCTA } from "@/components/site/Footer";
import { DemoForm } from "@/components/site/DemoForm";
import { Reveal, Kicker, SectionHeading } from "@/components/site/primitives";
import { CASE_STUDIES } from "@/resourcesData";

export default function Resources() {
  const [demoOpen, setDemoOpen] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="resources-page">
      <Navbar onBookDemo={() => setDemoOpen(true)} />
      <main className="pt-28">
        <section className="mx-auto max-w-[1400px] px-6 py-16 lg:px-10 lg:py-24">
          <Reveal>
            <Kicker>Resources · Customer stories</Kicker>
            <SectionHeading className="mt-6 max-w-3xl">
              Built from experience. <span className="text-volt">Proven on the floor.</span>
            </SectionHeading>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-500">
              Real operations, real numbers. See how warehouses like yours took control of
              inventory, capacity and pallets with Stockaris.
            </p>
          </Reveal>

          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {CASE_STUDIES.map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.08}>
                <Link
                  to={`/resources/${c.slug}`}
                  data-testid={`case-card-${c.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-lg border border-slate-200 bg-white transition-colors hover:border-volt/50 hover:shadow-[0_30px_70px_-40px_rgba(47,68,232,0.4)]"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img src={c.image} alt={c.company} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-[#0B1240]/45" />
                    <div className="absolute bottom-4 left-4">
                      <div className="font-mono-p text-[10px] uppercase tracking-widest text-white/70">{c.industry}</div>
                      <div className="font-display text-xl font-bold text-white">{c.company}</div>
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-display text-xl font-bold leading-snug text-slate-900">{c.headline}</h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-500">{c.summary}</p>
                    <div className="mt-6 flex flex-wrap gap-4">
                      {c.metrics.map((m) => (
                        <div key={m.k}>
                          <div className="font-display text-2xl font-black text-volt">{m.v}</div>
                          <div className="font-mono-p text-[10px] uppercase tracking-widest text-slate-400">{m.k}</div>
                        </div>
                      ))}
                    </div>
                    <span className="mt-6 inline-flex items-center gap-1.5 font-mono-p text-[11px] uppercase tracking-widest text-volt">
                      Read story <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
        <FinalCTA onBookDemo={() => setDemoOpen(true)} />
      </main>
      <Footer />
      <DemoForm open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}
